import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Download, ArrowRight } from "lucide-react";
import { api, API, getToken, formatZAR, MONTHS } from "../api";
import BusinessFilter from "../components/BusinessFilter";
import { useBusiness } from "../context/BusinessContext";

const SHORT_MONTHS = MONTHS.map(m => m.slice(0, 3));

export default function AnnualTotals() {
  const { business } = useBusiness();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const yearOptions = useMemo(() => {
    const cur = new Date().getFullYear();
    return [cur + 1, cur, cur - 1, cur - 2, cur - 3, cur - 4];
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { year, compare_previous: true };
    if (business) params.business = business;
    api.get("/summary/annual", { params })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [year, business]);

  const downloadCsv = () => {
    const qp = new URLSearchParams({ year: String(year) });
    if (business) qp.set("business", business);
    const url = `${API}/invoices/export.csv?${qp.toString()}`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then(r => r.blob())
      .then(blob => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `invoices-${year}${business ? `-${business.replace(/\s+/g, "_")}` : ""}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="np-label mb-2">Annual totals</div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
            Year {year}
          </h1>
          <p className="text-[#595959] mt-2">
            Invoiced totals per client per month{business ? ` · ${business} only` : ""}. Outstanding work is excluded.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="np-card !p-3">
            <BusinessFilter />
          </div>
          <select
            data-testid="annual-year-select"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3] font-bold"
          >
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <button
            data-testid="annual-download-csv"
            onClick={downloadCsv}
            className="bg-[#0F4F51] hover:bg-[#011617] text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center gap-2"
          >
            <Download className="w-4 h-4" /> Download CSV
          </button>
        </div>
      </div>

      {/* Top-line KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KpiCard label={`Invoiced ${year}`} value={data ? formatZAR(data.year_total) : "—"} testid="annual-total-current" />
        <KpiCard label={`Invoiced ${year - 1}`} value={data ? formatZAR(data.prev_year_total || 0) : "—"} testid="annual-total-previous" />
        <DeltaCard
          delta={data ? (data.year_total - (data.prev_year_total || 0)) : 0}
          base={data?.prev_year_total || 0}
          testid="annual-total-delta"
        />
      </div>

      {/* Table */}
      <div className="np-card overflow-x-auto" data-testid="annual-table-card">
        {loading && <p className="text-sm text-[#595959]">Loading…</p>}
        {!loading && (!data || data.rows.length === 0) && (
          <p className="text-sm text-[#595959]">No invoiced work recorded for {year}{business ? ` · ${business}` : ""}.</p>
        )}
        {!loading && data && data.rows.length > 0 && (
          <table className="w-full text-sm min-w-[1200px]" data-testid="annual-table">
            <thead className="np-table-header">
              <tr>
                <th className="text-left px-3 py-2.5 sticky left-0 bg-[#F4F4F4]">Client</th>
                {SHORT_MONTHS.map(m => (
                  <th key={m} className="text-right px-3 py-2.5">{m}</th>
                ))}
                <th className="text-right px-3 py-2.5 font-extrabold">{year}</th>
                <th className="text-right px-3 py-2.5 text-[#595959]">{year - 1}</th>
                <th className="text-right px-3 py-2.5">Δ</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.client_id} className="border-b border-[#CCCCCC] hover:bg-[#E5FFFF]/40" data-testid={`annual-row-${row.client_id}`}>
                  <td className="px-3 py-2 font-semibold text-[#011617] sticky left-0 bg-white">
                    <Link to={`/workspace?client_id=${row.client_id}&year=${year}&month=${new Date().getMonth() + 1}`}
                          className="hover:text-[#00ACB3] inline-flex items-center gap-1">
                      {row.client_name}
                      <ArrowRight className="w-3 h-3 opacity-50" />
                    </Link>
                    <div className="text-[10px] uppercase tracking-wider text-[#595959]">{row.business}</div>
                  </td>
                  {row.months.map((amt, i) => (
                    <td key={i} className={`px-3 py-2 text-right font-mono text-xs ${amt > 0 ? "text-[#011617]" : "text-[#CCCCCC]"}`}>
                      {amt > 0 ? formatZAR(amt) : "—"}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right font-extrabold text-[#0F4F51]">
                    {formatZAR(row.year_total)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-[#595959]">
                    {row.prev_year_total ? formatZAR(row.prev_year_total) : "—"}
                  </td>
                  <td className={`px-3 py-2 text-right font-bold text-xs ${
                    (row.delta || 0) > 0 ? "text-[#065F46]" :
                    (row.delta || 0) < 0 ? "text-[#991B1B]" : "text-[#595959]"
                  }`}>
                    {row.delta != null && row.prev_year_total > 0
                      ? `${row.delta >= 0 ? "+" : ""}${formatZAR(row.delta)}`
                      : "—"}
                  </td>
                </tr>
              ))}
              {/* Totals row */}
              <tr className="bg-[#E5FFFF] font-bold" data-testid="annual-totals-row">
                <td className="px-3 py-2.5 sticky left-0 bg-[#E5FFFF]">All clients</td>
                {(data.monthly_totals || []).map((t, i) => (
                  <td key={i} className="px-3 py-2.5 text-right font-mono text-xs">
                    {t > 0 ? formatZAR(t) : "—"}
                  </td>
                ))}
                <td className="px-3 py-2.5 text-right text-base font-extrabold text-[#0F4F51]">{formatZAR(data.year_total)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs text-[#595959]">{formatZAR(data.prev_year_total || 0)}</td>
                <td className={`px-3 py-2.5 text-right text-xs font-bold ${
                  (data.year_total - (data.prev_year_total || 0)) > 0 ? "text-[#065F46]" :
                  (data.year_total - (data.prev_year_total || 0)) < 0 ? "text-[#991B1B]" : "text-[#595959]"
                }`}>
                  {data.prev_year_total > 0
                    ? `${(data.year_total - data.prev_year_total) >= 0 ? "+" : ""}${formatZAR(data.year_total - data.prev_year_total)}`
                    : "—"}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, testid }) {
  return (
    <div className="np-card" data-testid={testid}>
      <div className="np-label">{label}</div>
      <div className="text-2xl font-extrabold text-[#011617] mt-2" style={{ fontFamily: "Manrope" }}>{value}</div>
    </div>
  );
}

function DeltaCard({ delta, base, testid }) {
  const positive = delta >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  const pct = base > 0 ? ((delta / base) * 100).toFixed(1) : null;
  return (
    <div className="np-card" data-testid={testid}>
      <div className="np-label">Year-over-year</div>
      <div className={`text-2xl font-extrabold mt-2 inline-flex items-center gap-2 ${positive ? "text-[#065F46]" : "text-[#991B1B]"}`} style={{ fontFamily: "Manrope" }}>
        <Icon className="w-5 h-5" />
        {delta >= 0 ? "+" : ""}{formatZAR(delta)}
      </div>
      <div className="text-xs text-[#595959] mt-1">
        {pct != null ? `${positive ? "+" : ""}${pct}% vs previous year` : "no prior-year data"}
      </div>
    </div>
  );
}
